"use client";
import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import { Link } from "@/i18n/navigation";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";

import { ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const caseStudies = [
  {
    key: "taksverden",
    href: "/case-study/taksverden" as const,
    tags: ["Travel & Tourism", "Web Application", "Branding"],
  },
  {
    key: "iti",
    href: "/case-study/iti" as const,
    tags: ["E-Commerce", "Headless CMS", "Native Apps"],
  },
];

const ServicesWork = () => {
  const t = useTranslations("services");
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <section ref={sectionRef} className="section pb-[60px] lg:pb-[100px] relative">
      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div className="text-center mb-[40px] lg:mb-[56px]" variants={itemVariants}>
            <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
              {t("work.sectionTitle")}
            </h2>
            <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
              {t("work.sectionDescription")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-[20px] max-w-[900px] mx-auto">
            {caseStudies.map((cs) => (
              <motion.div key={cs.key} variants={itemVariants}>
                <Link href={cs.href} className="block h-full group">
                  <AnimatedCard
                    className="rounded-[18px] lg:rounded-[22px] h-full"
                    circleClassName="w-[120px] h-[120px] blur-[70px]"
                  >
                    <BorderWrapper
                      wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                      className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] overflow-hidden relative flex flex-col"
                    >
                      <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
                      <div className="relative z-10">
                        <h3 className="text-[20px] lg:text-[24px] font-semibold leading-[1.25] text-white mb-[10px]">
                          {t(`work.${cs.key}.title`)}
                        </h3>
                        <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 mb-[20px]">
                          {t(`work.${cs.key}.description`)}
                        </p>
                        <div className="flex flex-wrap gap-[6px] mb-[20px]">
                          {cs.tags.map((tag) => (
                            <span key={tag} className="px-[10px] py-[4px] rounded-full text-[11px] font-light border border-white/10 text-foreground/40">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <span className="inline-flex items-center text-[14px] font-light text-foreground/50 group-hover:text-white transition-colors duration-300">
                          {t("work.viewCaseStudy")}
                        </span>
                      </div>
                    </BorderWrapper>
                  </AnimatedCard>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div variants={itemVariants} className="text-center mt-[32px]">
            <Link
              href="/blog"
              className="flex items-center justify-center gap-[8px] text-[15px] font-light text-foreground/50 hover:text-white transition-colors duration-300 group"
            >
              {t("work.readBlog")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesWork;
